import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import SetCredentialsAside from "@/modules/aside/SetCredentialsAside.vue";
import Onboarding from "@/modules/Onboarding.vue";
import EmailSent from "@/modules/messages/EmailSent.vue";
import EmailError from "@/modules/messages/EmailError.vue";
import TokenExpired from "@/modules/messages/TokenExpired.vue";
import VerifyEmailError from "@/modules/messages/VerifyEmailError.vue";
import SetCredentialsError from "@/modules/messages/SetCredentialsError.vue";
import InternalServerError from "@/modules/messages/InternalServerError.vue";
import store, { StoreActions } from "@/store";
import SetCredentials from "@/modules/forms/SetCredentials.vue";

import {
  onboardingService,
  OnboardingStatus,
} from "@/services/onboarding/onboarding-service";
import { configService } from "@/services/config-service";
import { Tier, TierName } from "@/services/onboarding/model/tierName";
import MarketplaceAccountAlreadyActivated from "@/modules/messages/MarketplaceAccountNotActivated.vue";
import MarketplaceOrganizationExists from "@/modules/messages/MarketplaceOrganizationExists.vue";
import MarketplaceTokenExpired from "@/modules/messages/MarketplaceTokenExpired.vue";
import RegisterForm from "@/modules/forms/RegisterForm.vue";
import StagedTokenExpiredError from "@/modules/messages/StagedTokenExpiredError.vue";
import StagedLicenseNotFoundError from "@/modules/messages/StagedLicenseNotFoundError.vue";

export enum RoutesName {
  SIGNUP = "signup",
  EMAIL_SENT = "email-sent",
  EMAIL_ERROR = "email-error",
  ONBOARDING_ROUTER = "onboarding-router",
  SET_CREDENTIALS = "set-credentials",
  SET_CREDENTIALS_ERROR = "set-credentials-error",
  VERIFY_EMAIL_ERROR = "verify-email-error",
  TOKEN_EXPIRED = "token-expired",
  STAGED_TOKEN_EXPIRED_ERROR = "staged-token-expired-error",
  STAGED_LICENSE_NOT_FOUND = "stage-license-not-found",
  INTERNAL_SERVER_ERROR = "internal-server-error",
  MARKETPLACE_TOKEN_EXPIRED = "marketplace-token-expired",
  MARKETPLACE_ORGANIZATION_EXISTS = "marketplace-organization-exists",
  MARKETPLACE_ACCOUNT_NOT_ACTIVATED = "marketplace-account-not-activated",
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: RoutesName.SIGNUP,
    component: Onboarding,
    props: (route) => ({
      tier: new Tier(route.query.teams ? TierName.teams : TierName.free),
      awsMarketplaceToken: route.query.awsMarketplaceToken,
    }),
    children: [
      {
        path: "",
        components: {
          default: RegisterForm,
        },
        meta: {
          step: 0,
        },
      },
      {
        path: "email-sent",
        name: RoutesName.EMAIL_SENT,
        component: EmailSent,
        beforeEnter: (to, from, next) => {
          const prospect = store.getters.prospect;
          if (prospect && prospect.email !== "") {
            return next();
          }
          next();
        },
      },
      {
        path: "email-error",
        name: RoutesName.EMAIL_ERROR,
        component: EmailError,
      },
      {
        path: "token-expired",
        name: RoutesName.TOKEN_EXPIRED,
        component: TokenExpired,
      },
      {
        path: "staged-token-expired-error",
        name: RoutesName.STAGED_TOKEN_EXPIRED_ERROR,
        component: StagedTokenExpiredError,
      },
      {
        path: "stage-license-not-found",
        name: RoutesName.STAGED_LICENSE_NOT_FOUND,
        component: StagedLicenseNotFoundError,
      },
      {
        path: "marketplace-token-expired",
        name: RoutesName.MARKETPLACE_TOKEN_EXPIRED,
        component: MarketplaceTokenExpired,
      },
      {
        path: "marketplace-organization-exists",
        name: RoutesName.MARKETPLACE_ORGANIZATION_EXISTS,
        component: MarketplaceOrganizationExists,
      },
      {
        path: "marketplace-account-not-activated",
        name: RoutesName.MARKETPLACE_ACCOUNT_NOT_ACTIVATED,
        component: MarketplaceAccountAlreadyActivated,
      },
      {
        path: "confirm-email/:jwt",
        name: RoutesName.SET_CREDENTIALS,
        components: {
          default: SetCredentials,
          aside: SetCredentialsAside,
        },
        beforeEnter: async (to, _from, next) => {
          const jwt = to.params.jwt as string;

          const status = await onboardingService.status(jwt).catch(() => {
            next({ name: RoutesName.TOKEN_EXPIRED });
          });

          if (!status) {
            throw new Error("Unknown status, unable to proceed");
          }

          if (status.status === OnboardingStatus.accountProvisioned) {
            window.location.href = configService.signInUrl;
          }

          store.dispatch(StoreActions.setProspect, status);
          store.dispatch(StoreActions.setJwt, jwt);

          await onboardingService.verifyEmail(jwt).catch(() => {
            next({ name: "verify-email-error" });
          });
          const recoveryQuestions =
            await onboardingService.getRecoveryQuestions(jwt);
          store.dispatch(StoreActions.setRecoveryQuestions, recoveryQuestions);
          next();
        },
      },
      {
        path: "set-credentials-error",
        name: RoutesName.SET_CREDENTIALS_ERROR,
        component: SetCredentialsError,
      },
      {
        path: "verify-email-error",
        name: RoutesName.VERIFY_EMAIL_ERROR,
        component: VerifyEmailError,
      },
      {
        path: "internal-server-error",
        name: RoutesName.INTERNAL_SERVER_ERROR,
        component: InternalServerError,
      },
    ],
  },
  { path: "/:catchAll(.*)", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

export default router;
